import React, { useEffect, useState } from "react";
import { ThinDropdownWithSearch } from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

const Drainage = ({ houseHoldDrainage, DqaApproved, DqaRejected }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const householdDrainageId = houseHoldDrainage?.household_drainage_id || null;

  const [wasteWater, setWasteWater] = useState("");
  const [pool, setPool] = useState(undefined); // boolean required
  const [houseFlooded, setHouseFlooded] = useState(undefined); // boolean required
  const [streetFlooded, setStreetFlooded] = useState(undefined); // boolean required
  const [sphfBeneficiary, setSphfBeneficiary] = useState(undefined); // boolean required
  const [constructions, setConstructions] = useState(""); // required if sphfBeneficiary === true
  const [anotherShelter, setAnotherShelter] = useState(undefined); // required if sphfBeneficiary === true

  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected);
  const [householdInfoData, setHouseholdInfoData] = useState(null);

  // errors
  const [errors, setErrors] = useState({});


  useEffect(() => {
    // Normalize incoming values to booleans where appropriate
    const toBool = (v) =>
      v === true || v === 1 || v === "1"
        ? true
        : v === false || v === 0 || v === "0"
        ? false
        : undefined;

    setWasteWater(houseHoldDrainage?.water_drained_out || "");
    setPool(toBool(houseHoldDrainage?.stagnant_water_in_village));
    setHouseFlooded(toBool(houseHoldDrainage?.house_flooded));
    setStreetFlooded(toBool(houseHoldDrainage?.street_flooded));
    setSphfBeneficiary(toBool(houseHoldDrainage?.sphf_registered_beneficiary));
    setConstructions(houseHoldDrainage?.construction_status || "");
    setAnotherShelter(toBool(houseHoldDrainage?.has_another_shelter));
  }, [houseHoldDrainage]);

  const handleWaterWasteChange = (value) => {
    setWasteWater(value);
    setErrors((p) => ({ ...p, wasteWater: undefined }));
  };
  const handleConstructionChange = (value) => {
    setConstructions(value);
    setErrors((p) => ({ ...p, constructions: undefined }));
  };
  const handlePool = (value) => {
    setPool(value);
    setErrors((p) => ({ ...p, pool: undefined }));
  };
  const handleHouseFlooded = (value) => {
    setHouseFlooded(value);
    setErrors((p) => ({ ...p, houseFlooded: undefined }));
  };
  const handleStreetFlooded = (value) => {
    setStreetFlooded(value);
    setErrors((p) => ({ ...p, streetFlooded: undefined }));
  };
  const handleSphfBeneficiary = (value) => {
    setSphfBeneficiary(value);
    setErrors((p) => ({ ...p, sphfBeneficiary: undefined }));
    if (value === false) {
      setConstructions("");
      setAnotherShelter(undefined);
      setErrors((p) => ({ ...p, constructions: undefined, anotherShelter: undefined }));
    }
  };
  const handleAnotherShelter = (value) => {
    setAnotherShelter(value);
    setErrors((p) => ({ ...p, anotherShelter: undefined }));
  };

  const drained = [
    { value: "Piped drainage", label: "Piped drainage" },
    { value: "Soak pit", label: "Soak pit" },
    { value: "Road side drain/nali", label: "Road side drain/nali" },
  ];
  const construction = [
    { value: "Not started", label: "Not started" },
    { value: "Ongoing", label: "Ongoing" },
    { value: "Paused", label: "Paused" },
    { value: "Completed", label: "Completed" },
  ];

  // ---------- VALIDATION ----------
  const validate = () => {
    const e = {};
    const req = (v) => v !== undefined && v !== null && String(v).trim() !== "";

    if (!req(wasteWater)) e.wasteWater = "Waste Water is Required.";

    if (pool !== true && pool !== false) e.pool = "Please select Yes or No.";

    if (houseFlooded !== true && houseFlooded !== false)
      e.houseFlooded = "Please select Yes or No.";
    if (streetFlooded !== true && streetFlooded !== false)
      e.streetFlooded = "Please select Yes or No.";

    if (sphfBeneficiary !== true && sphfBeneficiary !== false)
      e.sphfBeneficiary = "Please select Yes or No.";
    
    if (sphfBeneficiary === true) {
      if (!req(constructions))
        e.constructions = "Please select construction status.";
      if (anotherShelter !== true && anotherShelter !== false) {
        e.anotherShelter = "Please select Yes or No.";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdateDrainage = async () => {
    if (!validate()) {
      return;
    }
    try {
      const payload = {
        household_drainage_id: householdDrainageId,
        household_information_id: id,
        water_drained_out: wasteWater,
        stagnant_water_in_village: pool,
        house_flooded: houseFlooded,
        street_flooded: streetFlooded,
        sphf_registered_beneficiary: sphfBeneficiary,
        construction_status: sphfBeneficiary === true ? constructions : null,
        has_another_shelter: sphfBeneficiary === true ? anotherShelter : null,
      };
      const res = await fetchData(
        `householddrainage-update/${id}`,
        "PUT",
        payload
      );
      if (res.success) {
        toast.success("Drainage updated successfully");
      } else {
        toast.error(res.message || "Failed to update");
      }
    } catch (error) {
      toast.error("An error occurred while updating");
      console.error(error);
    }
  };

  const isDataValid =
    houseHoldDrainage &&
    (wasteWater ||
      pool !== undefined ||
      houseFlooded !== undefined ||
      streetFlooded !== undefined ||
      sphfBeneficiary !== undefined ||
      anotherShelter !== undefined ||
      constructions);

  const Err = (name) =>
    errors?.[name] ? (
      <p className="text-[0.9rem] text-red-600 mt-2">{errors[name]}</p>
    ) : null;

  return (
    <div className=" bg-white ">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}

      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Drainage
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Drainage
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      {houseHoldDrainage ? (
        <form className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 1. Where is house waste water drained-out? */}
            <div>
              <ThinDropdownWithSearch
                label="Where is house waste water drained-out?"
                options={drained}
                value={wasteWater}
                onChange={handleWaterWasteChange}
                required
                disabled={isDqaApproved || isDqaRejected}
              />
              {Err("wasteWater")}
            </div>

            {/* 2. Is there pools of stagnant water in your village/mohalla? */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Is there pools of stagnant water in your village/mohalla?{" "}
                {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="pool"
                    value={true}
                    checked={pool === true}
                    onChange={() => handlePool(true)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="pool"
                    value={false}
                    checked={pool === false}
                    onChange={() => handlePool(false)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
              {Err("pool")}
            </div>

            {/* 3. Was your house flooded during the recent rains? */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Was your house flooded during the recent rains?{" "}
                {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="house-flooded"
                    value={true}
                    checked={houseFlooded === true}
                    onChange={() => handleHouseFlooded(true)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="house-flooded"
                    value={false}
                    checked={houseFlooded === false}
                    onChange={() => handleHouseFlooded(false)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
              {Err("houseFlooded")}
            </div>

            {/* 4. Was your street flooded during the recent rains? */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Was your street flooded during the recent rains?{" "}
                {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="street-flooded"
                    value={true}
                    checked={streetFlooded === true}
                    onChange={() => handleStreetFlooded(true)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="street-flooded"
                    value={false}
                    checked={streetFlooded === false}
                    onChange={() => handleStreetFlooded(false)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
              {Err("streetFlooded")}
            </div>

            {/* 5. Are you a registered SPHF beneficiary? */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Are you a registered SPHF beneficiary?{" "}
                {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="sphf-beneficiary"
                    value={true}
                    checked={sphfBeneficiary === true}
                    onChange={() => handleSphfBeneficiary(true)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    name="sphf-beneficiary"
                    value={false}
                    checked={sphfBeneficiary === false}
                    onChange={() => handleSphfBeneficiary(false)}
                    className="accent-blue-600"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
              {Err("sphfBeneficiary")}
            </div>

            {/* Conditional fields when SPHF beneficiary is Yes */}
            {sphfBeneficiary === true && (
              <>
                {/* 6. If yes, what is the status of construction? */}
                <div>
                  <ThinDropdownWithSearch
                    label="If yes, what is the status of construction?"
                    options={construction}
                    value={constructions}
                    onChange={handleConstructionChange}
                    required
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  {Err("constructions")}
                </div>

                {/* 7. If yes, do you have another shelter? */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    If yes, do you have another shelter?{" "}
                    {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="another-shelter"
                        value={true}
                        checked={anotherShelter === true}
                        onChange={() => handleAnotherShelter(true)}
                        className="accent-blue-600"
                        disabled={isDqaApproved || isDqaRejected}
                      />
                      <span className="text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="another-shelter"
                        value={false}
                        checked={anotherShelter === false}
                        onChange={() => handleAnotherShelter(false)}
                        className="accent-blue-600"
                        disabled={isDqaApproved || isDqaRejected}
                      />
                      <span className="text-sm text-gray-700">No</span>
                    </label>
                  </div>
                  {Err("anotherShelter")}
                </div>
              </>
            )}
          </div>

          {isDataValid && !isDqaApproved && !isDqaRejected && (
            <div className="mt-8 flex justify-end">
              <button
                type="button"
                onClick={handleUpdateDrainage}
                className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors"
              >
                Update
              </button>
            </div>
          )}
        </form>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 italic text-lg">
            No drainage data available for this household
          </p>
        </div>
      )}
    </div>
  );
};

export default Drainage;